import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { pagesTenantCanDisable } from '@aeris/permissions'
import { CheckboxField } from '@aeris/ui'

import {
  fetchPlatformAccountPagesOff,
  setPlatformAccountPagesOff,
} from '../lib/api/platform.ts'
import { GROUP_LABELS, GROUP_ORDER } from '../lib/module-groups.ts'
import { MODULES } from '../lib/modules.tsx'

/**
 * As telas do núcleo que esta conta tem (F172).
 *
 * Marcada = a conta tem. O padrão é ter tudo — é o que deixa toda conta que já
 * existia do jeito que estava —, e o operador desmarca o que o cliente não usa
 * (a fábrica de uniformes não usa Trocas, Canais, Atendimento).
 *
 * Agrupadas como o menu, pela mesma lista de `MODULES` que a Equipe usa, e só
 * as que `pagesTenantCanDisable` aceita — o servidor recusa o resto.
 */
export const AccountPages = ({ tenantId }: { tenantId: string }): ReactNode => {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['platform-pages-off', tenantId],
    queryFn: ({ signal }) => fetchPlatformAccountPagesOff(tenantId, signal),
  })

  const save = useMutation({
    mutationFn: async (pagesOff: readonly string[]) =>
      await setPlatformAccountPagesOff(tenantId, pagesOff),
    onSuccess: (pagesOff) => {
      queryClient.setQueryData(['platform-pages-off', tenantId], pagesOff)
      setError(null)
    },
    onError: () => {
      setError('Não foi possível salvar. Tente de novo.')
    },
  })

  const off = new Set(query.data ?? [])
  const disableable = new Set(pagesTenantCanDisable())
  const sections = GROUP_ORDER.map((group) => ({
    group,
    entries: MODULES.filter(
      (entry) => entry.group === group && disableable.has(entry.module)
    ),
  })).filter((section) => section.entries.length > 0)

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        Telas desta conta
      </h3>
      <p className="text-(length:--text-xs) text-(--text-muted)">
        Desmarcar tira a tela de todo mundo desta conta, e as rotas dela passam
        a recusar. Quem estava nela vê &ldquo;não liberada&rdquo; na próxima vez
        que abrir. Nada é apagado; marcar de novo traz tudo de volta.
      </p>

      {error !== null && (
        <p className="text-(length:--text-xs) text-(--danger)">{error}</p>
      )}

      {sections.map((section) => (
        <fieldset key={section.group} className="flex flex-col gap-1">
          <legend className="mb-1 text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
            {GROUP_LABELS[section.group]}
          </legend>
          <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
            {section.entries.map((entry) => (
              <CheckboxField
                key={entry.module}
                label={entry.label}
                checked={!off.has(entry.module)}
                disabled={query.isPending || save.isPending}
                onCheckedChange={(checked) => {
                  const next = new Set(off)
                  if (checked === true) next.delete(entry.module)
                  else next.add(entry.module)
                  save.mutate([...next])
                }}
              />
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  )
}
